import { useEffect, useMemo, useRef, useState } from 'react';
import type maplibregl from 'maplibre-gl';
import { telemetryApi } from '../../services/telemetryApi';
import type { TacticalEvent } from '../../components/dashboard/types';
import type { DashboardAirstrip, ReplayLeg } from './types';
import { LEG_COLORS, haversineNm } from './dashboardUtils';
import { toReplayEvents } from './useDashboardReplayHelpers';

interface UseDashboardReplayParams {
  selectedAircraft: { id: string; tailNumber: string } | null;
  activeTab: 'events' | 'history' | 'calculations';
  mapRef: React.MutableRefObject<maplibregl.Map | null>;
  mapAirstrips: DashboardAirstrip[];
}

export const useDashboardReplay = ({
  selectedAircraft,
  activeTab,
  mapRef,
  mapAirstrips,
}: UseDashboardReplayParams) => {
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyReplayData, setHistoryReplayData] = useState<TacticalEvent[]>([]);
  const [isLoadingReplay, setIsLoadingReplay] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [isReplaying, setIsReplaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1);

  const currentReplayRowRef = useRef<HTMLTableRowElement>(null);
  const replayTrailAddedRef = useRef(false);
  const replayFullTrailAddedRef = useRef(false);
  const replayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getNearestAirstrip = (lat: number, lng: number): string | null => {
    if (!lat || !lng) return null;
    for (const a of mapAirstrips) {
      const dLat = (a.lat - lat) * 111000;
      const dLng = (a.lng - lng) * 111000 * Math.cos(lat * Math.PI / 180);
      if (Math.sqrt(dLat * dLat + dLng * dLng) < 3000) return a.name || a.icao || a.code;
    }
    return null;
  };

  const replayStats = useMemo(() => {
    if (historyReplayData.length < 2) return null;
    let totalNm = 0;
    let airborneMs = 0;
    let landings = 0;
    let maxAlt = 0;
    let maxSpeed = 0;
    let wasFlying = false;

    for (let i = 1; i < historyReplayData.length; i++) {
      const prev = historyReplayData[i - 1];
      const curr = historyReplayData[i];
      if (prev.lat && prev.lng && curr.lat && curr.lng) totalNm += haversineNm(prev.lat, prev.lng, curr.lat, curr.lng);
      const isFlying = curr.speed > 30;
      if (isFlying) {
        const dt = new Date(curr.timestamp).getTime() - new Date(prev.timestamp).getTime();
        if (dt > 0 && dt < 7200000) airborneMs += dt;
      }
      if (wasFlying && !isFlying) landings++;
      wasFlying = isFlying;
      if ((curr.altitude || 0) > maxAlt) maxAlt = curr.altitude || 0;
      if (curr.speed > maxSpeed) maxSpeed = curr.speed;
    }

    return {
      totalNm: Math.round(totalNm * 10) / 10,
      flightTimeMin: Math.round(airborneMs / 60000),
      landings,
      maxAlt: Math.round(maxAlt),
      maxSpeed: Math.round(maxSpeed),
      totalPoints: historyReplayData.length,
    };
  }, [historyReplayData]);

  const replayLegs = useMemo((): ReplayLeg[] => {
    if (historyReplayData.length < 2) return [];
    const MIN_GROUND_MS = 3 * 60 * 1000;
    const legs: ReplayLeg[] = [];
    let current: TacticalEvent[] = [];
    let groundSince: number | null = null;

    for (let i = 0; i < historyReplayData.length; i++) {
      const pt = historyReplayData[i];
      const flying = (pt.speed || 0) > 30;
      if (flying) {
        if (groundSince !== null) {
          const groundMs = new Date(pt.timestamp).getTime() - groundSince;
          if (groundMs >= MIN_GROUND_MS && current.length >= 2) {
            legs.push({ points: current, color: LEG_COLORS[legs.length % LEG_COLORS.length] });
            current = [];
          }
          groundSince = null;
        }
        current.push(pt);
      } else {
        if (groundSince === null && current.length > 0) {
          groundSince = new Date(pt.timestamp).getTime();
        }
        current.push(pt);
      }
    }

    if (current.length >= 2) legs.push({ points: current, color: LEG_COLORS[legs.length % LEG_COLORS.length] });
    return legs.length > 0 ? legs : [{ points: historyReplayData, color: LEG_COLORS[0] }];
  }, [historyReplayData]);

  const replayRouteStops = useMemo((): string[] => {
    if (replayLegs.length === 0) return [];
    const stops: string[] = [];
    replayLegs.forEach((leg, idx) => {
      const first = leg.points.find((p: any) => p.lat && p.lng);
      const last = [...leg.points].reverse().find((p: any) => p.lat && p.lng);
      if (idx === 0 && first) {
        const name = getNearestAirstrip(first.lat!, first.lng!);
        stops.push(name || '?');
      }
      if (last) {
        const name = getNearestAirstrip(last.lat!, last.lng!);
        stops.push(name || '?');
      }
    });
    return stops.filter((s, i) => i === 0 || s !== stops[i - 1]);
  }, [replayLegs, mapAirstrips]);

  useEffect(() => {
    setHistoryReplayData([]);
    setReplayIndex(0);
    setIsReplaying(false);
    if (replayIntervalRef.current) clearInterval(replayIntervalRef.current);
    try {
      const s1 = mapRef.current?.getSource('replay-trail') as maplibregl.GeoJSONSource | undefined;
      if (s1) s1.setData({ type: 'FeatureCollection', features: [] });
      const s2 = mapRef.current?.getSource('replay-full-trail') as maplibregl.GeoJSONSource | undefined;
      if (s2) s2.setData({ type: 'FeatureCollection', features: [] });
    } catch {}
  }, [selectedAircraft?.id, mapRef]);

  useEffect(() => {
    if (!isReplaying || historyReplayData.length === 0) {
      if (replayIntervalRef.current) clearInterval(replayIntervalRef.current);
      return;
    }
    const intervalMs = Math.max(120, 800 / replaySpeed);
    replayIntervalRef.current = setInterval(() => {
      setReplayIndex((prev) => {
        if (prev >= historyReplayData.length - 1) {
          setIsReplaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, intervalMs);
    return () => {
      if (replayIntervalRef.current) clearInterval(replayIntervalRef.current);
    };
  }, [isReplaying, replaySpeed, historyReplayData.length]);

  useEffect(() => {
    if (activeTab === 'history') {
      currentReplayRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [replayIndex, activeTab]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    if (historyReplayData.length === 0) {
      try {
        const src = map.getSource('replay-full-trail') as maplibregl.GeoJSONSource | undefined;
        if (src) src.setData({ type: 'FeatureCollection', features: [] });
      } catch {}
      return;
    }

    if (!replayFullTrailAddedRef.current) {
      try {
        map.addSource('replay-full-trail', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'replay-full-trail-glow', type: 'line', source: 'replay-full-trail', paint: { 'line-color': ['get', 'color'], 'line-width': 24, 'line-opacity': 0.28, 'line-blur': 14 } });
        map.addLayer({ id: 'replay-full-trail-casing', type: 'line', source: 'replay-full-trail', paint: { 'line-color': '#000000', 'line-width': 10, 'line-opacity': 0.80, 'line-cap': 'round', 'line-join': 'round' } });
        map.addLayer({ id: 'replay-full-trail-line', type: 'line', source: 'replay-full-trail', paint: { 'line-color': '#ffffff', 'line-width': 4.5, 'line-opacity': 1.0, 'line-cap': 'round', 'line-join': 'round' } });
        map.addLayer({ id: 'replay-full-trail-accent', type: 'line', source: 'replay-full-trail', paint: { 'line-color': ['get', 'color'], 'line-width': 1.8, 'line-opacity': 1.0, 'line-cap': 'round', 'line-join': 'round' } });
        replayFullTrailAddedRef.current = true;
      } catch {}
    }

    const features = replayLegs
      .map((leg) => {
        const coords: [number, number][] = leg.points.filter((p: any) => p.lat && p.lng).map((p: any) => [p.lng, p.lat]);
        return { type: 'Feature' as const, geometry: { type: 'LineString' as const, coordinates: coords }, properties: { color: leg.color } };
      })
      .filter((f) => f.geometry.coordinates.length >= 2);

    const src = map.getSource('replay-full-trail') as maplibregl.GeoJSONSource | undefined;
    if (src) src.setData({ type: 'FeatureCollection', features });

    const allCoords = features.flatMap((f) => f.geometry.coordinates as [number, number][]);
    if (allCoords.length >= 2) {
      const lngs = allCoords.map((c) => c[0]);
      const lats = allCoords.map((c) => c[1]);
      try {
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 70, maxZoom: 14, duration: 1400 }
        );
      } catch {}
    }
  }, [historyReplayData, replayLegs, mapRef]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    if (historyReplayData.length === 0) {
      try {
        const src = map.getSource('replay-trail') as maplibregl.GeoJSONSource | undefined;
        if (src) src.setData({ type: 'FeatureCollection', features: [] });
      } catch {}
      return;
    }

    if (!replayTrailAddedRef.current) {
      try {
        map.addSource('replay-trail', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'replay-trail-glow', type: 'line', source: 'replay-trail', paint: { 'line-color': '#f59e0b', 'line-width': 8, 'line-opacity': 0.18, 'line-blur': 5 } });
        map.addLayer({ id: 'replay-trail-line', type: 'line', source: 'replay-trail', paint: { 'line-color': '#f59e0b', 'line-width': 2.5, 'line-opacity': 0.92 } });
        replayTrailAddedRef.current = true;
      } catch {
        return;
      }
    }

    const src = map.getSource('replay-trail') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;

    const trailPoints = historyReplayData.slice(0, replayIndex + 1);
    const coords = trailPoints.filter((ev) => ev.lat && ev.lng).map((ev) => [ev.lng!, ev.lat!] as [number, number]);
    src.setData(
      coords.length >= 2
        ? {
            type: 'FeatureCollection',
            features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} }],
          }
        : { type: 'FeatureCollection', features: [] }
    );

    const ev = historyReplayData[replayIndex];
    if (ev?.lat && ev?.lng && isReplaying) {
      const panDuration = Math.max(80, Math.round(650 / Math.max(replaySpeed, 1)));
      try {
        map.panTo([ev.lng, ev.lat], { duration: panDuration });
      } catch {}
    }
  }, [replayIndex, historyReplayData, isReplaying, replaySpeed, mapRef]);

  const fetchHistoryReplay = async () => {
    if (!selectedAircraft?.tailNumber || !historyDateFrom) return;
    setIsLoadingReplay(true);
    setHistoryReplayData([]);
    setReplayIndex(0);
    setIsReplaying(false);
    try {
      const s1 = mapRef.current?.getSource('replay-trail') as maplibregl.GeoJSONSource | undefined;
      if (s1) s1.setData({ type: 'FeatureCollection', features: [] });
      const s2 = mapRef.current?.getSource('replay-full-trail') as maplibregl.GeoJSONSource | undefined;
      if (s2) s2.setData({ type: 'FeatureCollection', features: [] });
    } catch {}
    try {
      const from = new Date(historyDateFrom).toISOString();
      const to = historyDateTo ? new Date(historyDateTo).toISOString() : new Date().toISOString();
      try {
        const replay = await telemetryApi.getReplay(selectedAircraft.tailNumber, from, to, 2000);
        if (replay?.path && Array.isArray(replay.path)) {
          const events: TacticalEvent[] = toReplayEvents(replay.path, { alreadyAscending: true });
          setHistoryReplayData(events);
        } else {
          setHistoryReplayData([]);
        }
      } catch {
        // Backward-compatible fallback for older backend nodes
        const raw = await telemetryApi.getHistoryRange(selectedAircraft.tailNumber, from, to, 2000);
        const events: TacticalEvent[] = toReplayEvents(raw);
        setHistoryReplayData(events);
      }
    } catch {
      setHistoryReplayData([]);
    } finally {
      setIsLoadingReplay(false);
    }
  };

  return {
    LEG_COLORS,
    currentReplayRowRef,
    historyDateFrom,
    historyDateTo,
    historyReplayData,
    isLoadingReplay,
    replayIndex,
    isReplaying,
    replaySpeed,
    replayStats,
    replayRouteStops,
    getNearestAirstrip,
    fetchHistoryReplay,
    setHistoryDateFrom,
    setHistoryDateTo,
    setReplayIndex,
    setIsReplaying,
    setReplaySpeed,
  };
};
